import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { useReportOutput } from "@/profitability/query";
import { structureChanges } from "./structure-changes";
import { VersionFigures } from "./version-figures";

const Lines = ({ lines }: { lines: string[] }) => (
  <ul className="list-disc space-y-1 pl-5">
    {lines.map((line) => (
      <li key={line}>
        <Text>{line}</Text>
      </li>
    ))}
  </ul>
);

/** The tabs, tables and columns publishing would change, read from both runs' output. */
const StructureChanges = ({
  businessId,
  publishedRunId,
  draftRunId,
}: {
  businessId: string;
  publishedRunId: string;
  draftRunId: string;
}) => {
  const published = useReportOutput(businessId, publishedRunId);
  const draft = useReportOutput(businessId, draftRunId);
  if (published.isPending || draft.isPending)
    return (
      <Skeleton className="h-16 w-full" aria-label="Loading the changes" />
    );
  // A run written before reports declared their output has no shape to compare.
  if (!published.data || !draft.data) return null;
  const lines = structureChanges(published.data, draft.data);
  return lines.length ? (
    <Lines lines={lines} />
  ) : (
    <Text tone="muted">The tabs, tables and columns are the same.</Text>
  );
};

/**
 * What publishing the draft would change: what Volca says it changed, the
 * report's shape, and the headline figures, each version run on the files
 * the template was built from.
 */
export const ChangesSection = ({
  businessId,
  publishedRunId,
  draftRunId,
  changeNotes,
}: {
  businessId: string;
  publishedRunId: string;
  draftRunId: string;
  changeNotes: string[];
}) => (
  <Stack gap={6} align="stretch" data-testid="changes-section">
    <Stack gap={2} align="stretch">
      <Heading level={3}>What Volca changed</Heading>
      {changeNotes.length ? (
        <Lines lines={changeNotes} />
      ) : (
        <Text tone="muted">
          This draft was built before Volca listed its changes: compare the
          report and its figures below.
        </Text>
      )}
    </Stack>
    <Stack gap={2} align="stretch">
      <Heading level={3}>Tabs, tables and columns</Heading>
      <StructureChanges
        businessId={businessId}
        publishedRunId={publishedRunId}
        draftRunId={draftRunId}
      />
    </Stack>
    <Stack gap={2} align="stretch">
      <Heading level={3}>Headline figures</Heading>
      <VersionFigures
        businessId={businessId}
        publishedRunId={publishedRunId}
        draftRunId={draftRunId}
      />
    </Stack>
  </Stack>
);
